import { InquiryService, SyncResult } from './inquiryService';
import { GoogleSheetsService } from './googleSheetsService';

export interface AutoSyncStatus {
  isRunning: boolean;
  lastSyncTime: Date | null;
  nextSyncTime: Date | null;
  syncInterval: number; // in milliseconds
  lastSyncResult: SyncResult | null;
  errorCount: number;
  totalSyncs: number;
}

export interface ManualSyncResult extends SyncResult {
  syncTime: Date;
  source: 'manual' | 'automatic';
}

export class AutoSyncService {
  private static instance: AutoSyncService | null = null;
  private intervalId: NodeJS.Timeout | null = null;
  private status: AutoSyncStatus = {
    isRunning: false,
    lastSyncTime: null,
    nextSyncTime: null,
    syncInterval: 60000, // 1 minute in milliseconds
    lastSyncResult: null,
    errorCount: 0,
    totalSyncs: 0
  };
  private listeners: Set<(status: AutoSyncStatus) => void> = new Set();

  private constructor() {}

  /**
   * Get singleton instance
   */
  static getInstance(): AutoSyncService {
    if (!this.instance) {
      this.instance = new AutoSyncService();
    }
    return this.instance;
  }

  /**
   * Start automatic sync every minute
   */
  start(): void {
    if (this.status.isRunning) {
      console.log('⚠️ Auto-sync is already running');
      return;
    }

    console.log('🚀 Starting automatic inquiry sync (every 1 minute)...');
    
    this.status.isRunning = true;
    this.status.nextSyncTime = new Date(Date.now() + this.status.syncInterval);
    this.notifyListeners();

    // Run initial sync
    this.performSync('automatic');

    // Set up interval for recurring syncs
    this.intervalId = setInterval(() => {
      this.performSync('automatic');
    }, this.status.syncInterval);
  }

  /**
   * Stop automatic sync
   */
  stop(): void {
    if (!this.status.isRunning) {
      console.log('⚠️ Auto-sync is not running');
      return;
    }

    console.log('🛑 Stopping automatic inquiry sync...');
    
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.status.isRunning = false;
    this.status.nextSyncTime = null;
    this.notifyListeners();
  }

  /**
   * Perform manual sync
   */
  async performManualSync(): Promise<ManualSyncResult> {
    console.log('🔄 Manual sync requested...');
    
    const result = await this.performSync('manual');
    
    return {
      ...result,
      syncTime: new Date(),
      source: 'manual'
    };
  }

  /**
   * Get current sync status
   */
  getStatus(): AutoSyncStatus {
    return { ...this.status };
  }

  /**
   * Subscribe to sync status updates
   */
  subscribe(callback: (status: AutoSyncStatus) => void): () => void {
    this.listeners.add(callback);
    
    // Send initial status
    callback(this.getStatus());
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Update sync interval (in minutes)
   */
  setSyncInterval(minutes: number): void {
    if (minutes < 1) {
      throw new Error('Sync interval must be at least 1 minute');
    }

    const newInterval = minutes * 60 * 1000; // Convert to milliseconds
    this.status.syncInterval = newInterval;

    // If running, restart with new interval
    if (this.status.isRunning) {
      this.stop();
      this.start();
    }

    console.log(`⏰ Sync interval updated to ${minutes} minute(s)`);
  }

  /**
   * Test Google Sheets connection
   */
  async testConnection(): Promise<boolean> {
    try {
      console.log('🔍 Testing Google Sheets connection...');
      
      const isConnected = await GoogleSheetsService.testConnection();
      
      if (isConnected) {
        console.log('✅ Google Sheets connection successful');
      } else {
        console.log('❌ Google Sheets connection failed');
      }
      
      return isConnected;
    } catch (error) {
      console.error('❌ Error testing Google Sheets connection:', error);
      return false;
    }
  }

  /**
   * Get preview of Google Sheets data
   */
  async getPreview(): Promise<{
    headers: string[];
    sampleRows: any[];
    totalEstimatedRows: number;
  }> {
    try {
      console.log('👁️ Getting Google Sheets preview...');
      
      const preview = await GoogleSheetsService.getPreview();
      
      console.log(`📊 Preview: ${preview.sampleRows.length} sample rows, ~${preview.totalEstimatedRows} total rows`);
      
      return preview;
    } catch (error) {
      console.error('❌ Error getting Google Sheets preview:', error);
      throw error;
    }
  }

  /**
   * Validate Google Sheets structure
   */
  async validateSheetStructure(): Promise<{
    isValid: boolean;
    errors: string[];
    headers: string[];
  }> {
    try {
      console.log('🔍 Validating Google Sheets structure...');
      
      const validation = await GoogleSheetsService.validateSheetStructure();
      
      if (validation.isValid) {
        console.log('✅ Google Sheets structure is valid');
      } else {
        console.log('❌ Google Sheets structure validation failed:', validation.errors);
      }
      
      return validation;
    } catch (error) {
      console.error('❌ Error validating Google Sheets structure:', error);
      throw error;
    }
  }

  /**
   * Get sync statistics
   */
  getSyncStats(): {
    totalSyncs: number;
    errorCount: number;
    successRate: number;
    lastSyncTime: Date | null;
    isRunning: boolean;
    nextSyncTime: Date | null;
  } {
    const successRate = this.status.totalSyncs > 0 
      ? ((this.status.totalSyncs - this.status.errorCount) / this.status.totalSyncs) * 100
      : 0;

    return {
      totalSyncs: this.status.totalSyncs,
      errorCount: this.status.errorCount,
      successRate: Math.round(successRate),
      lastSyncTime: this.status.lastSyncTime,
      isRunning: this.status.isRunning,
      nextSyncTime: this.status.nextSyncTime
    };
  }

  /**
   * Reset sync statistics
   */
  resetStats(): void {
    this.status.totalSyncs = 0;
    this.status.errorCount = 0;
    this.status.lastSyncResult = null;
    this.notifyListeners();
  }

  /**
   * Internal method to perform sync
   */
  private async performSync(source: 'manual' | 'automatic'): Promise<SyncResult> {
    const syncStartTime = new Date();
    
    try {
      console.log(`🔄 Starting ${source} sync...`);
      
      // Update status
      this.status.totalSyncs++;
      this.status.lastSyncTime = syncStartTime;
      
      if (this.status.isRunning) {
        this.status.nextSyncTime = new Date(Date.now() + this.status.syncInterval);
      }
      
      this.notifyListeners();

      // Perform the actual sync
      // TEMPORARILY DISABLED: const result = await InquiryService.processNewInquiries();
      const result = { totalRows: 0, newRows: 0, processedRows: 0, errors: [] };
      
      // Update status with result
      this.status.lastSyncResult = result;
      
      const syncEndTime = new Date();
      const duration = syncEndTime.getTime() - syncStartTime.getTime();
      
      console.log(`✅ ${source} sync completed in ${duration}ms:`, {
        newInquiries: result.newInquiries,
        autoLinkedTickets: result.autoLinkedTickets,
        errors: result.errors.length
      });
      
      // Log errors if any
      if (result.errors.length > 0) {
        console.warn('⚠️ Sync completed with errors:', result.errors);
      }
      
      this.notifyListeners();
      
      return result;
      
    } catch (error) {
      console.error(`❌ ${source} sync failed:`, error);
      
      this.status.errorCount++;
      this.status.lastSyncResult = {
        newInquiries: 0,
        linkedInquiries: 0,
        autoLinkedTickets: 0,
        errors: [`Sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`]
      };
      
      this.notifyListeners();
      
      throw error;
    }
  }

  /**
   * Notify all listeners of status changes
   */
  private notifyListeners(): void {
    const currentStatus = this.getStatus();
    this.listeners.forEach(callback => {
      try {
        callback(currentStatus);
      } catch (error) {
        console.error('Error in sync status listener:', error);
      }
    });
  }
}

// Export singleton instance
export const autoSyncService = AutoSyncService.getInstance();

// Auto-start sync when module is imported (can be disabled if needed)
export const startAutoSync = () => {
  autoSyncService.start();
};

export const stopAutoSync = () => {
  autoSyncService.stop();
};

// Helper function to initialize sync with custom interval
export const initializeAutoSync = (intervalMinutes: number = 1) => {
  autoSyncService.setSyncInterval(intervalMinutes);
  autoSyncService.start();
};

// Helper function to perform manual sync
export const performManualSync = async (): Promise<ManualSyncResult> => {
  return autoSyncService.performManualSync();
};

// Helper function to get sync status
export const getSyncStatus = (): AutoSyncStatus => {
  return autoSyncService.getStatus();
};

// Helper function to subscribe to sync updates
export const subscribeToSyncUpdates = (callback: (status: AutoSyncStatus) => void): () => void => {
  return autoSyncService.subscribe(callback);
}; 